import { Dialog, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContactService } from "../../../services/api";
import FormInput from "../../molecules/FormInput";
import FormTemplate from "../../templates/FormTemplate";
import useRefresh from "../../../hooks/useRefresh";
import { Address, Contact } from "../../../services/swagger";
import useBreakpoint from "../../../hooks/useBreakpoint";
import useConfirm from "../../../hooks/useConfirm";
import DeleteIcon from "@mui/icons-material/Delete";
import { useMutation } from "@tanstack/react-query";
import { useBusinessStore } from "../../../stores/Dummy";

interface EditContactDialogProps {
  contact?: Contact;
  onSubmit?: () => void;
  onClose?: () => void;
}

export default function EditContactDialog(props: EditContactDialogProps) {
  const { contact } = props;
  const { t } = useTranslation("Contact");
  const refresh = useRefresh();
  const [company, setCompany] = useState("");
  const [title, setTitle] = useState("");
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phones, setPhones] = useState<string[]>([]);
  const [position, setPosition] = useState("");
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [extRef, setExtRef] = useState("");
  const miniCompanyList = useBusinessStore((state) => state.miniCompanyList);
  const confirm = useConfirm();

  const breakpoint = useBreakpoint();

  useEffect(() => {
    if (!contact) return;
    setTitle(contact.title);
    setFirstName(contact.firstName);
    setMiddleName(contact.middleName);
    setLastName(contact.lastName);
    setEmail(contact.email);
    setPhones(contact.phones);
    setPosition(contact.position);
    setAddresses(contact.addresses);
    setExtRef(contact.extRef);
    if (contact.company?._id) setCompany(contact.company._id);
  }, [props.contact]);

  const { mutate: handleSubmit } = useMutation({
    mutationFn: async () => {
      if (!contact?._id) return;
      const res = await ContactService.updateContact({
        id: contact._id,
        updateContactBody: {
          company,
          addresses,
          email,
          extRef,
          firstName,
          lastName,
          middleName,
          phones,
          position,
          title,
        },
      });
      return res.data.data;
    },
    onSuccess: (data) => {
      if (data) {
        refresh();
        props.onClose?.();
      }
    },
  });

  const { mutate: handleDelete } = useMutation({
    mutationFn: async () => {
      if (!contact?._id) return;
      const res = await ContactService.deleteContact({
        id: contact._id,
      });
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) {
        refresh();
        props.onClose?.();
      }
    },
  });

  return (
    <Dialog
      fullScreen={!breakpoint.sm}
      open={Boolean(props.contact)}
      onClose={props.onClose}
    >
      <FormTemplate
        title={t("EditContact")}
        onSubmit={handleSubmit}
        onCancel={props.onClose}
        options={[
          {
            icon: <DeleteIcon color="error" />,
            label: t("DeleteContact"),
            onClick: () => confirm(handleDelete),
          },
        ]}
        confirm
      >
        <Stack spacing={2} sx={{ minWidth: { xs: 1, sm: 400, md: 500 } }}>
          <FormInput
            field="company"
            label={t("Company")}
            onChange={(v) => {
              setCompany(v as string);
            }}
            type="list"
            list={[
              {
                name: "—",
                value: "",
              },
              ...miniCompanyList.map?.((company) => ({
                name: company.name,
                value: company._id,
              })),
            ]}
            value={company}
          />
          <FormInput
            field="title"
            label={t("Title")}
            onChange={(v) => {
              setTitle(v as string);
            }}
            type="string"
            value={title}
          />
          <FormInput
            field="firstName"
            label={t("FirstName")}
            onChange={(v) => {
              setFirstName(v as string);
            }}
            type="string"
            value={firstName}
          />
          <FormInput
            field="middleName"
            label={t("MiddleName")}
            onChange={(v) => {
              setMiddleName(v as string);
            }}
            type="string"
            value={middleName}
          />
          <FormInput
            field="lastName"
            label={t("LastName")}
            onChange={(v) => {
              setLastName(v as string);
            }}
            type="string"
            value={lastName}
          />
          <FormInput
            field="email"
            label={t("Email")}
            onChange={(v) => {
              setEmail(v as string);
            }}
            type="string"
            value={email}
          />
          <FormInput
            field="phones"
            label={t("Phones")}
            onChange={(v) => {
              setPhones(v as string[]);
            }}
            type="chip-list"
            value={phones}
          />
          <FormInput
            field="position"
            label={t("Position")}
            onChange={(v) => {
              setPosition(v as string);
            }}
            type="string"
            value={position}
          />
          <FormInput
            field="extRef"
            label={t("ExtRef")}
            onChange={(v) => {
              setExtRef(v as string);
            }}
            type="string"
            value={extRef}
          />
        </Stack>
      </FormTemplate>
    </Dialog>
  );
}
